/**
 * Valuation Lines Tooltip
 *
 * Displays enabled valuation lines:
 * - Average (mean/median/harmonic)
 * - ±1σ, ±2σ, ±3σ (Standard deviation bands)
 */

import { format } from "d3-format";
import React, { Component } from "react";
import PropTypes from "prop-types";

import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { functor, isDefined } from "../utils";

class ValuationLinesTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }

  calculateValuationLines(plotData, sourcePath, averageType) {
    // Extract values from visible plotData
    const values = [];
    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const value = d[sourcePath];
      if (isDefined(value) && isFinite(value)) {
        values.push(value);
      }
    }

    if (values.length < 2) return null;

    // Calculate average
    let average;
    if (averageType === "median") {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      average =
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
    } else if (averageType === "harmonic") {
      const nonZero = values.filter((v) => v !== 0);
      if (nonZero.length === 0) return null;
      const sumOfReciprocals = nonZero.reduce((acc, val) => acc + 1 / val, 0);
      average = nonZero.length / sumOfReciprocals;
    } else {
      // mean (arithmetic)
      const sum = values.reduce((acc, val) => acc + val, 0);
      average = sum / values.length;
    }

    // Calculate standard deviation
    const squaredDiffs = values.map((v) => Math.pow(v - average, 2));
    const avgSquaredDiff =
      squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
    const stdDev = Math.sqrt(avgSquaredDiff);

    return {
      average: average,
      plusOneStdDev: average + stdDev,
      minusOneStdDev: average - stdDev,
      plusTwoStdDev: average + 2 * stdDev,
      minusTwoStdDev: average - 2 * stdDev,
      plusThreeStdDev: average + 3 * stdDev,
      minusThreeStdDev: average - 3 * stdDev,
    };
  }

  renderSVG(moreProps) {
    const {
      onClick,
      origin: originProp,
      className,
      fontFamily,
      fontSize,
      labelFill,
      textFill,
      displayFormat,
      options,
      appearance,
      labels,
    } = this.props;

    const origin = functor(originProp);
    const {
      chartConfig: { width, height },
      plotData,
    } = moreProps;
    const [x, y] = origin(width, height);

    // Calculate from visible plotData instead of using yAccessor
    const sourcePath = options?.sourcePath || "close";
    const averageType = options?.averageType || "mean";
    const value = this.calculateValuationLines(
      plotData,
      sourcePath,
      averageType,
    );

    // Format values with n/a fallback
    const averageValue =
      value && Number.isFinite(value.average)
        ? displayFormat(value.average)
        : "n/a";
    const plus1Value =
      value && Number.isFinite(value.plusOneStdDev)
        ? displayFormat(value.plusOneStdDev)
        : "n/a";
    const minus1Value =
      value && Number.isFinite(value.minusOneStdDev)
        ? displayFormat(value.minusOneStdDev)
        : "n/a";
    const plus2Value =
      value && Number.isFinite(value.plusTwoStdDev)
        ? displayFormat(value.plusTwoStdDev)
        : "n/a";
    const minus2Value =
      value && Number.isFinite(value.minusTwoStdDev)
        ? displayFormat(value.minusTwoStdDev)
        : "n/a";
    const plus3Value =
      value && Number.isFinite(value.plusThreeStdDev)
        ? displayFormat(value.plusThreeStdDev)
        : "n/a";
    const minus3Value =
      value && Number.isFinite(value.minusThreeStdDev)
        ? displayFormat(value.minusThreeStdDev)
        : "n/a";

    // Get average type from options
    const calcAverageType = options?.averageType || "mean";
    const averageTypeLabel =
      calcAverageType === "median"
        ? "mediana"
        : calcAverageType === "harmonic"
          ? "harmônica"
          : "aritmética";

    // Get display flags from appearance
    const displayAverage = appearance?.displayAverage !== false;
    const displayOneStdDev = appearance?.displayOneStdDev === true;
    const displayTwoStdDev = appearance?.displayTwoStdDev === true;
    const displayThreeStdDev = appearance?.displayThreeStdDev === true;

    // Get colors from appearance
    const averageColor = appearance?.strokeAverage?.color || "#00afed";
    const plus1Color = appearance?.strokePlusOne?.color || "#e1e1e1";
    const minus1Color = appearance?.strokeMinusOne?.color || "#e1e1e1";
    const plus2Color = appearance?.strokePlusTwo?.color || "#85c99e";
    const minus2Color = appearance?.strokeMinusTwo?.color || "#85c99e";
    const plus3Color = appearance?.strokePlusThree?.color || "#fff69e";
    const minus3Color = appearance?.strokeMinusThree?.color || "#fff69e";

    // Get labels from props (passed from registry) or use defaults
    const averageLabel = labels?.average || "Média";
    const plus1Label = labels?.plusOneStdDev || "+1σ";
    const minus1Label = labels?.minusOneStdDev || "-1σ";
    const plus2Label = labels?.plusTwoStdDev || "+2σ";
    const minus2Label = labels?.minusTwoStdDev || "-2σ";
    const plus3Label = labels?.plusThreeStdDev || "+3σ";
    const minus3Label = labels?.minusThreeStdDev || "-3σ";

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-valuation-lines"
      >
        <ToolTipText
          fontFamily={fontFamily}
          fontSize={fontSize}
          fill={textFill}
        >
          <ToolTipTSpanLabel fill={labelFill}>Linhas Aval.</ToolTipTSpanLabel>
          <ToolTipTSpanLabel
            fill={labelFill}
          >{` (${averageTypeLabel})`}</ToolTipTSpanLabel>

          {displayAverage && (
            <>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${averageLabel}: `}</ToolTipTSpanLabel>
              <tspan fill={averageColor}>{averageValue}</tspan>
            </>
          )}

          {displayOneStdDev && (
            <>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${plus1Label}: `}</ToolTipTSpanLabel>
              <tspan fill={plus1Color}>{plus1Value}</tspan>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${minus1Label}: `}</ToolTipTSpanLabel>
              <tspan fill={minus1Color}>{minus1Value}</tspan>
            </>
          )}

          {displayTwoStdDev && (
            <>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${plus2Label}: `}</ToolTipTSpanLabel>
              <tspan fill={plus2Color}>{plus2Value}</tspan>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${minus2Label}: `}</ToolTipTSpanLabel>
              <tspan fill={minus2Color}>{minus2Value}</tspan>
            </>
          )}

          {displayThreeStdDev && (
            <>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${plus3Label}: `}</ToolTipTSpanLabel>
              <tspan fill={plus3Color}>{plus3Value}</tspan>
              <ToolTipTSpanLabel
                fill={labelFill}
              >{`  ${minus3Label}: `}</ToolTipTSpanLabel>
              <tspan fill={minus3Color}>{minus3Value}</tspan>
            </>
          )}
        </ToolTipText>
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

ValuationLinesTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  labelFill: PropTypes.string,
  textFill: PropTypes.string,
  displayFormat: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
  options: PropTypes.object,
  appearance: PropTypes.object,
  labels: PropTypes.shape({
    average: PropTypes.string,
    plusOneStdDev: PropTypes.string,
    minusOneStdDev: PropTypes.string,
    plusTwoStdDev: PropTypes.string,
    minusTwoStdDev: PropTypes.string,
    plusThreeStdDev: PropTypes.string,
    minusThreeStdDev: PropTypes.string,
  }),
};

ValuationLinesTooltip.defaultProps = {
  origin: [0, 10],
  className:
    "react-stockcharts-tooltip react-stockcharts-valuation-lines-tooltip",
  fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
  fontSize: 11,
  labelFill: "#4682B4",
  textFill: "#000000",
  displayFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  onClick: undefined,
};

export default ValuationLinesTooltip;
